import React from 'react';
import { graphql } from 'gatsby';
import Typography from '@mui/material/Typography';
import { createIntl, IntlProvider } from 'react-intl';
import { useLanguageLink } from '../../hooks/useLanguageLink';
import ar from './ar.json';
import en from './en.json';


import { DNGatsbyTemplate } from '../DriveNinjaTemplate/gatsby';
import { LocaleProvider } from '../../../contexts/language';
import Stack from '@mui/material/Stack';
import LazyLoad from 'react-lazyload';
import 'react-multi-carousel/lib/styles.css';
import Skeleton from '@mui/material/Skeleton';
import { searchParamsToLinkFormat } from '../../../functions/searchParamsToLinkFormat';
import { EditorJSBlock } from '../../molecules/EditorJSBlock/editorJSBlock';
import allFields from '../../../config/allFields.json';

const SearchFilterCarousel = React.lazy(() => import('./searchFilterCarousel'));
const languages = {
  ar, en
};
const boolFields = allFields.filter(f => f.type === 'bool').map(f => f.slug);

const JargonDetailPage = ({ pageContext: { lang, direction, ...filters }, data }) => {
  const intl = createIntl({ locale: lang || 'en', messages: languages[lang] || languages.en });
  const l = useLanguageLink();
  const { pageTitle, slug, content } = data?.driveNinjaJargonEntryType || {};

  const searchFilters = Object.keys(filters).filter(f => f.startsWith('filter_')).filter(f => filters[f] === true).map(f => f.slice(7)).filter(f => boolFields.indexOf(f) !== -1).reduce((a, b) => {
    const n = { ...a };
    n[b] = true;
    return n;
  }, {});
  return (<LocaleProvider localeConfig={{ lang, direction }}>
    <DNGatsbyTemplate
      breadcrumbs={[{
        name: intl.formatMessage({
          id: 'breadcrumbs.learnToSpeakCar', defaultMessage: 'Learn To Speak Car'
        }), path: l('/learn-to-speak-car/')
      }, {
        name: pageTitle, path: l(`/learn-to-speak-car/${slug}`)
      }]}

    >
      <IntlProvider locale={lang} messages={languages[lang] || languages.en}>
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Typography variant={'h1'}>{pageTitle}</Typography>
          {content.blocks.map(b => b.data).map((b, id) => {
            return (<EditorJSBlock key={id} {...b} />);
          })}
          {Object.keys(searchFilters)?.length ? (
            <LazyLoad height={690}>
              <React.Suspense fallback={<Skeleton variant="rectangular" width={'100%'} height={690} />}>
                <SearchFilterCarousel title={`Cars with ${pageTitle}`}
                  groupedOffers={data?.allDriveNinjaPurchaseNewOfferType?.group}
                  search={searchParamsToLinkFormat({ searchParameters:searchFilters })} />
              </React.Suspense>
            </LazyLoad>
          ) : null}
        </Stack>
      </IntlProvider>
    </DNGatsbyTemplate>
  </LocaleProvider>);
};
export default JargonDetailPage;

export const query = graphql`
    query JargonItemQuery($slug: String!,$filter_rear_passenger_ac_vents: Boolean, $filter_crash_warning_system: Boolean, $filter_electronic_stability_control: Boolean, $filter_usb_input: Boolean, $filter_split_folding_rear_seats: Boolean, $filter_paddle_shifter: Boolean, $filter_hands_free_tailgate: Boolean, $filter_panoramic_sunroof: Boolean, $filter_leather_seats: Boolean, $filter_sports_seats: Boolean, $filter_automatic_air_conditioning: Boolean, $filter_aux_input: Boolean, $filter_touchscreen: Boolean, $filter_isofix_child_seat: Boolean, $filter_roof_rack_rails: Boolean, $filter_automatic_folding_wing_mirrors: Boolean, $filter_automatic_headlights: Boolean, $filter_daytime_running_lights: Boolean, $filter_remote_vehicle_start: Boolean, $filter_cooled_seats: Boolean, $filter_lane_departure_assist: Boolean, $filter_rear_fog_lights: Boolean, $filter_leather_wrapped_steering_wheel: Boolean, $filter_electric_wing_mirrors: Boolean, $filter_wireless_charging: Boolean, $filter_voice_control: Boolean, $filter_headlamps_laser: Boolean, $filter_front_fog_lights: Boolean, $filter_headlamps_led: Boolean, $filter_adaptive_cruise_control: Boolean, $filter_cruise_control: Boolean, $filter_front_parking_sensors: Boolean, $filter_camera_360: Boolean, $filter_push_button_start: Boolean, $filter_navigation: Boolean, $filter_hill_descent_assist: Boolean, $filter_reversing_camera: Boolean, $filter_electric_driver_seat: Boolean, $filter_blind_spot_warning: Boolean, $filter_apple_car_play: Boolean, $filter_sunroof_front: Boolean, $filter_hill_start_assist: Boolean, $filter_android_auto: Boolean, $filter_head_up_display: Boolean, $filter_rear_windscreen_shade: Boolean, $filter_keyless_entry: Boolean, $filter_electric_passenger_seat: Boolean, $filter_rear_parking_sensors: Boolean, $filter_bluetooth: Boolean, $filter_electronic_parking_brake: Boolean, $filter_wifi_connection: Boolean) {

        allDriveNinjaPurchaseNewOfferType(
            filter: {available: {eq: true},
                trimLevel: {keylessEntry: {eq: $filter_keyless_entry}, isofixChildSeat: {eq: $filter_isofix_child_seat}, touchscreen: {eq: $filter_touchscreen}, frontParkingSensors: {eq: $filter_front_parking_sensors}, camera360: {eq: $filter_camera_360}, bluetooth: {eq: $filter_bluetooth}, rearFogLights: {eq: $filter_rear_fog_lights}, androidAuto: {eq: $filter_android_auto}, electricDriverSeat: {eq: $filter_electric_driver_seat}, electronicStabilityControl: {eq: $filter_electronic_stability_control}, electronicParkingBrake: {eq: $filter_electronic_parking_brake}, rearPassengerAcVents: {eq: $filter_rear_passenger_ac_vents}, usbInput: {eq: $filter_usb_input}, crashWarningSystem: {eq: $filter_crash_warning_system}, laneDepartureAssist: {eq: $filter_lane_departure_assist}, paddleShifter: {eq: $filter_paddle_shifter}, appleCarPlay: {eq: $filter_apple_car_play}, automaticHeadlights: {eq: $filter_automatic_headlights}, roofRackRails: {eq: $filter_roof_rack_rails}, wirelessCharging: {eq: $filter_wireless_charging}, cruiseControl: {eq: $filter_cruise_control}, reversingCamera: {eq: $filter_reversing_camera}, leatherWrappedSteeringWheel: {eq: $filter_leather_wrapped_steering_wheel}, automaticFoldingWingMirrors: {eq: $filter_automatic_folding_wing_mirrors}, cooledSeats: {eq: $filter_cooled_seats}, sportsSeats: {eq: $filter_sports_seats}, automaticAirConditioning: {eq: $filter_automatic_air_conditioning}, panoramicSunroof: {eq: $filter_panoramic_sunroof}, handsFreeTailgate: {eq: $filter_hands_free_tailgate}, hillDescentAssist: {eq: $filter_hill_descent_assist}, electricWingMirrors: {eq: $filter_electric_wing_mirrors}, navigation: {eq: $filter_navigation}, daytimeRunningLights: {eq: $filter_daytime_running_lights}, remoteVehicleStart: {eq: $filter_remote_vehicle_start}, splitFoldingRearSeats: {eq: $filter_split_folding_rear_seats}, rearParkingSensors: {eq: $filter_rear_parking_sensors}, electricPassengerSeat: {eq: $filter_electric_passenger_seat}, sunroofFront: {eq: $filter_sunroof_front}, rearWindscreenShade: {eq: $filter_rear_windscreen_shade}, leatherSeats: {eq: $filter_leather_seats}, headlampsLed: {eq: $filter_headlamps_led}, hillStartAssist: {eq: $filter_hill_start_assist}, pushButtonStart: {eq: $filter_push_button_start}, headlampsLaser: {eq: $filter_headlamps_laser}, headUpDisplay: {eq: $filter_head_up_display}, voiceControl: {eq: $filter_voice_control}, blindSpotWarning: {eq: $filter_blind_spot_warning}, auxInput: {eq: $filter_aux_input}, wifiConnection: {eq: $filter_wifi_connection}, frontFogLights: {eq: $filter_front_fog_lights}, adaptiveCruiseControl: {eq: $filter_adaptive_cruise_control}}}
            limit: 20
        ) {
            group(field: {trimLevel: {subModel: {generation: {model:{slug: SELECT}}}}}) {
                edges {
                    node {
                        currency
                        available
                        trimLevel {
                            subModel {
                                generation {
                                    model {
                                        modelImages{
                                            orderingPriority
                                            localImage {
                                                childImageSharp {
                                                    gatsbyImageData(height: 200, layout: FIXED)
                                                    id
                                                }
                                            }
                                        }
                                        slug
                                        translatedName {
                                            ar
                                            en
                                        }
                                        manufacturer {
                                            slug
                                            translatedName {
                                                ar
                                                en
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                field
                fieldValue
                minPurchaseAmount: min(field: {purchaseAmount: SELECT})
                minMonthlyAmount: min(field: {monthlyAmount: SELECT})
                minAvailabilityRank: min(field: {stockAvailabilityRank: SELECT})
                totalCount
            }
            totalCount
        }
        allDriveNinjaModelType {
            totalCount
        }
        driveNinjaJargonEntryType(slug: {eq: $slug}) {
            slug
            updatedAt
            pageTitle
            featureSlug
            content {
                version
                time
                blocks {
                    ... on DriveNinjaEditorJSListType {
                        id
                        data {
                            style
                            items
                            remoteTypeName
                        }
                    }
                    ... on DriveNinjaEditorJSParagraphType {
                        id
                        data {
                            text
                            remoteTypeName
                        }
                    }
                    ... on DriveNinjaEditorJSHeadingType {
                        id
                        data {
                            text
                            remoteTypeName
                        }
                    }
                    ... on DriveNinjaEditorJSImageType {
                        id
                        data {
                            file {
                                localImage {
                                    childImageSharp {
                                        gatsbyImageData(layout: FULL_WIDTH)
                                        id
                                    }
                                }
                            }
                        }
                        type
                        remoteTypeName
                    }
                }
            }
        }
    }
`;

export const Head = ({ data, pageContext: { lang } }) => {
  const intl = createIntl({ locale: lang || 'en', messages: languages[lang] || languages.en });


  return (
    <>
      <meta name="description" content={intl.formatMessage({
        id: 'browsejargon.meta.description',
        defaultMessage: 'Find out everything you need to know about {pageTitle}'
      }, { pageTitle: data?.driveNinjaJargonEntryType?.pageTitle })} />
      <meta name="og:description" content={intl.formatMessage({
        id: 'browsejargon.meta.description',
        defaultMessage: 'Find out everything you need to know about {pageTitle}'
      }, { pageTitle: data?.driveNinjaJargonEntryType?.pageTitle })} />
      <meta name="og:title" content={data?.driveNinjaJargonEntryType?.pageTitle} />
      <title>
        {data?.driveNinjaJargonEntryType?.pageTitle}
      </title>
    </>
  );
};

